import React from "react";
//#region types
import { TranslationKeys } from "translations";
import { StyleEnums } from "models/enums";
import { PeriodisationRowsEditorProps } from "models/propTypes/incomingInvoicePropTypes";
import { PeriodisationRowFormValue } from "models/formTypes/incomingInvoice";
import { PeriodisationMode } from "models/utilTypes/periodisationTypes";
import { DateRange } from "models/formTypes/util";
import { IconDefinitionConstants } from "utils/constants";
//#endregion
//#region components
import { CurrencyInput } from "components/common/wintForms";
import Icon from "../icon";
import RoundButton from "../buttons/roundButton";
import MonthOrDateRangePicker from "../datePickers/monthOrDateRangePicker";
import usePeriodisationMinDate from "../forms/hooks/usePeriodisationMinDate";
//#endregion
import clsx from "clsx";
import cssExports from "theme/styles/main.module.scss";
import { LocalizationHelperInstance, PeriodisationRowsHelper } from "utils/helpers";

// Mirrors Core's IncomingInvoicePeriodisationRowValidator.MaxRows; the add row disappears at the limit.
const MaxRows = 20;

const noop = () => {};

// The whole ghost row is the button. Its look-alike inputs are inert so Tab and screen readers stop once, on the row,
// and a click on them lands on the row. React 18 has no typed `inert` prop; an empty string sets the attribute.
// React 19 treats inert as a boolean and drops an empty string, so this must become inert={true} on that upgrade.
const ghostContentProps = { inert: "" };

const PeriodisationRowsEditor: React.FC<PeriodisationRowsEditorProps> = (props) => {
  const { name, value, setValue, netAmount, currency, isCredit } = props;
  const minDate = usePeriodisationMinDate(value.mode);
  const rowsRef = React.useRef<HTMLDivElement>(null);
  const ghostPickerRef = React.useRef<HTMLDivElement>(null);
  // Indexes of rows whose amount field has been left; an empty or zero amount shows as an error only after that.
  const [touchedAmounts, setTouchedAmounts] = React.useState<number[]>([]);

  const setRow = (index: number, patch: Partial<PeriodisationRowFormValue>) =>
    setValue({ ...value, rows: value.rows.map((r, i) => (i === index ? { ...r, ...patch } : r)) });

  const setAmount = (index: number, raw: string) => {
    const amount = raw === "" || raw === "-" ? undefined : Number(raw);
    // The number field also reports programmatic value changes; only real edits should touch the value.
    if (amount === value.rows[index]?.amount) return;
    setRow(index, { amount });
  };

  // Mode belongs to the whole split (Core rejects mixed modes), so changing it clears every row's range.
  const setMode = (mode: PeriodisationMode) =>
    setValue({ ...value, mode, rows: value.rows.map((r) => ({ ...r, range: undefined })) });

  const ghostRowTestId = `${name}AddPeriodRow`;
  const amountInputSelector = (index: number) => `[data-testid="${name}.rows[${index}].amountBaseNumberInput"] input`;
  const pickerTriggerSelector = (index: number) =>
    `[data-testid="${name}.rows[${index}]"] .${cssExports.monthOrDateRangePickerTrigger}`;

  // The target row only exists (or holds its new data) after the next render, so focus moves on the following tick.
  const focusAfterRender = (selector: string) =>
    window.setTimeout(() => rowsRef.current?.querySelector<HTMLElement>(selector)?.focus(), 0);

  const openPickerAfterRender = (selector: string) =>
    window.setTimeout(() => rowsRef.current?.querySelector<HTMLElement>(selector)?.click(), 0);

  const addRow = (openPicker = false) => {
    const remaining = PeriodisationRowsHelper.remaining(value, netAmount);
    const rows = [...value.rows, { amount: remaining > 0 ? remaining : undefined }];
    setValue({ ...value, rows });
    if (openPicker) openPickerAfterRender(pickerTriggerSelector(rows.length - 1));
    else focusAfterRender(amountInputSelector(rows.length - 1));
  };

  // The ghost row's fields are inert, so the click lands on the row itself; its position says which field was meant.
  const onGhostRowClick = (event: React.MouseEvent) => {
    const picker = ghostPickerRef.current?.getBoundingClientRect();
    addRow(!!picker && event.clientX >= picker.left && event.clientX <= picker.right);
  };

  // Rows are keyed by index, so focus would otherwise stay on whatever remove button now sits at that index, or fall
  // to <body> when that button unmounts. After the last row the neighbour is the empty add row, always shown here.
  // The last row left takes the whole amount, since a single row is the plain range that periodises all of it.
  const removeRow = (index: number) => {
    const kept = value.rows.filter((_, i) => i !== index);
    const rows = kept.length === 1 ? [{ ...kept[0], amount: netAmount }] : kept;
    setValue({ ...value, rows });
    setTouchedAmounts((touched) => touched.filter((i) => i !== index).map((i) => (i > index ? i - 1 : i)));
    focusAfterRender(index < rows.length ? amountInputSelector(index) : `[data-testid="${ghostRowTestId}"]`);
  };

  const groupLabel = LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Accrual);
  const amountLabel = LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Amount);
  const addPeriodLabel = LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.AddPeriod);
  const removePeriodLabel = LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.RemovePeriod);
  const lockedTooltip = LocalizationHelperInstance.getFormattedMessage(
    TranslationKeys.Common.PeriodisationModeLockedToFirstRow
  );

  const renderRemoveButton = (index: number) =>
    value.rows.length > 1 && (
      <RoundButton
        size="xs"
        displayType={StyleEnums.DisplayType.None}
        className={cssExports.close}
        hoverText={removePeriodLabel}
        hoverTextAsToolTip
        onClick={() => removeRow(index)}
        testId={`${name}.rows[${index}].remove`}
      >
        <Icon icon={IconDefinitionConstants.WTimes} />
      </RoundButton>
    );

  return (
    <div className={cssExports.periodisationRows} ref={rowsRef} role="group" aria-label={groupLabel}>
      {value.rows.map((row, index) => (
        <div key={index} className={cssExports.periodisationRow} data-testid={`${name}.rows[${index}]`}>
          <div className={cssExports.periodisationRowAmount}>
            <CurrencyInput
              name={`${name}.rows[${index}].amount`}
              value={row.amount ?? ""}
              currency={currency}
              isCredit={isCredit}
              allowNegative={false}
              onChange={(e) => setAmount(index, `${e.target.value}`)}
              onBlur={() => setTouchedAmounts((touched) => (touched.includes(index) ? touched : [...touched, index]))}
              setValue={noop}
              setTouched={noop}
              touched
              hasError={touchedAmounts.includes(index) && !((row.amount ?? 0) > 0)}
              label={amountLabel}
            />
          </div>
          <div className={clsx(cssExports.periodisationRowPicker, cssExports.periodisationFillPicker)}>
            <MonthOrDateRangePicker
              mode={value.mode}
              onModeChange={setMode}
              range={row.range}
              onRangeChange={(range: DateRange | undefined) => setRow(index, { range })}
              minDate={minDate}
              modeToggleDisabled={index > 0}
              lockedTooltipText={index > 0 ? lockedTooltip : undefined}
            />
          </div>
          <div className={cssExports.periodisationRowRemove}>{renderRemoveButton(index)}</div>
        </div>
      ))}
      {value.rows.length < MaxRows && (
        // A <button> may not contain inputs, and the ghost row has to look like a real row (inputs included),
        // so it is a row with button semantics, a tab stop and Enter/Space handling instead.
        <div
          // eslint-disable-next-line jsx-a11y/prefer-tag-over-role -- see above
          role="button"
          tabIndex={0}
          aria-label={addPeriodLabel}
          className={clsx(cssExports.periodisationRow, cssExports.periodisationRowGhost)}
          onClick={onGhostRowClick}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              addRow();
            }
          }}
          data-testid={ghostRowTestId}
        >
          <div className={cssExports.periodisationRowAmount} {...ghostContentProps}>
            <CurrencyInput
              name={`${name}.ghost.amount`}
              value=""
              currency={currency}
              isCredit={isCredit}
              onChange={noop}
              onBlur={noop}
              setValue={noop}
              setTouched={noop}
              touched={false}
              hasError={false}
              label={amountLabel}
            />
          </div>
          <div
            ref={ghostPickerRef}
            className={clsx(cssExports.periodisationRowPicker, cssExports.periodisationFillPicker)}
            {...ghostContentProps}
          >
            <MonthOrDateRangePicker mode={value.mode} onModeChange={noop} range={undefined} onRangeChange={noop} />
          </div>
          <div className={cssExports.periodisationRowRemove} />
        </div>
      )}
    </div>
  );
};

export default PeriodisationRowsEditor;
